import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { GoogleLogin } from '@react-oauth/google';
import { ShareIcon, UserGroupIcon, SparklesIcon } from '@heroicons/react/24/outline';

export const Login: React.FC = () => {
  const { login, googleLogin } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      // Login to get the token and user data
      await login(email, password);
      
      // Force a page reload instead of navigation
      // This ensures the app initializes properly from localStorage
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to login');
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse: any) => {
    try {
      setError(null);
      setIsLoading(true);
      console.log('Starting Google authentication...');
      
      const response = await googleLogin(credentialResponse.credential);
      console.log('Google login response:', response);
      
      // Force a page reload to ensure proper initialization
      if (response.isNewUser || !response.user.profiles || response.user.profiles.length === 0) {
        // For new users or users without profiles, we need to create a profile
        console.log('New user or no profiles, redirecting to home...');
        window.location.href = '/';
      } else {
        // For existing users with profiles, just reload the page
        console.log('User has profiles, reloading page...');
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Google auth error:', err);
      setError(err instanceof Error ? err.message : 'Failed to log in with Google. Please try again.');
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    console.error('Google login failed');
    setError('Google login failed. Please try again or use email login.');
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Join or log into Say
          </h1>
          <div className="mt-4 text-sm text-gray-600 space-y-3">
            <div className="flex items-start space-x-3">
              <ShareIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <div>
                <p className="mb-1">Create multiple contact profiles for different moments —</p>
                <p className="mb-1">professional, friends, family, work, dating, events.</p>
                <p>Share the right details every time.</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <UserGroupIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p>Swap contact details instantly.</p>
            </div>
            <div className="flex items-start space-x-3">
              <SparklesIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
              <p>Get started for free now.</p>
            </div>
            <p className="font-medium mt-2">Use the one form below to signin or join with email or Google</p>
          </div>
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="mt-6 bg-white py-8 px-6 shadow sm:rounded-lg">
          {/* Google Sign-In Button */}
          <div className="flex justify-center">
            {isLoading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
              </div>
            ) : (
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap={false}
                theme="filled_blue"
                size="large"
                text="signin_with"
                width={isMobile ? "280" : "300"}
                shape="rectangular"
                context="signin"
                auto_select={false}
                ux_mode="popup"
                cancel_on_tap_outside={true}
              />
            )}
          </div>

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-500 font-medium">Or continue with email</span>
              </div>
            </div>

            <form className="mt-6 space-y-6" onSubmit={handleSubmit}>
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-colors duration-200"
                >
                  {isLoading ? 'Signing in...' : 'Sign in'}
                </button>
              </div>
            </form>

            {/* Trust/Reassurance Badges */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <div className="text-sm text-gray-600 space-y-3">
                <div className="flex items-center">
                  <span className="text-green-500 mr-2 text-lg">✅</span>
                  <p className="font-medium">We never save your contacts</p>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2 text-lg">✅</span>
                  <p className="font-medium">No AI, no tracking</p>
                </div>
                <div className="flex items-center">
                  <span className="text-green-500 mr-2 text-lg">✅</span>
                  <p className="font-medium">100% privacy-first</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center text-xs text-gray-500 mt-6">
          &copy; 2025 SayCard. All rights reserved.
          <div className="mt-2 space-x-4">
            <a href="/privacy" className="hover:text-gray-700">Privacy Policy</a>
            <a href="/terms" className="hover:text-gray-700">Terms of Service</a>
          </div>
        </div>
      </div>
    </div>
  );
};
